/* eslint-disable no-console */
import { ExportedRecordMetadata, FileExporterOption, FileExporterTableMetadata, TableExpandableCards } from '../models';
import { CSVFileExporterService, ExcelFileExporterService} from '.';
import { Injectable } from '@angular/core';
import { Observable, first, from, switchMap, tap } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { IModal } from '@domain/business-layer';
import { ToastDuration, ToastService } from '@shared';
import { FileExporterChoicePopupComponent, PdfConfigurationPage } from '../components';
import { TranslateService } from '@ngx-translate/core';
import { LoggingService } from '@shared/logging';
import { PdfFileExporterService } from './pdf-file-exporter.service';
import { PdfConfigData } from '../models/pdf-config-data.model';



@Injectable()
export class FileExporterService {
	public constructor(
		private excelFileExporterService: ExcelFileExporterService,
		private csvFileExporterService: CSVFileExporterService,
		private pdfFileExporterService: PdfFileExporterService,
		private modalController: ModalController,
		private toastService: ToastService,
		private translateService: TranslateService,
		private loggingService: LoggingService
	) {}

	public openExportOptionPopup$(): Observable<IModal> {
		return from(
			this.modalController.create({
				component: FileExporterChoicePopupComponent,
				backdropDismiss: true,
				initialBreakpoint: 0.4,
				showBackdrop: true
			})
		).pipe(
			first(),
			tap(async (modal) => await modal.present()),
			switchMap(async (modal) => await modal.onDidDismiss<string>())
		) as Observable<IModal>;
	}

	public async export(
		tables: FileExporterTableMetadata[],
		exportedRecordMetadata: ExportedRecordMetadata,
		reportName: string
		): Promise<void> {
		this.openExportOptionPopup$().subscribe(async (modal: IModal) => {
			try {
                if (modal.data === FileExporterOption.pdf) {
                    const pdfConfig = await this.openPdfConfigurationModal();
                    this.handlePdfExport(tables, exportedRecordMetadata, reportName, pdfConfig);
                } else {
                    this.handleFileExport(modal.data, tables, exportedRecordMetadata, reportName);
                }
            } catch (error) {
				this.toastService.presentError(
					this.translateService.instant('entry.export.errorMessages.exportFailed'),
					ToastDuration.Slow
				);

				this.loggingService.error(
					'Plugin Error: Error in creating and saving the export to the filesystem',
					error
				);
			}
		});
	}

	private async openPdfConfigurationModal(): Promise<PdfConfigData> {
        // Open the PdfConfigurationPage and return the selected options
        const modal = await this.modalController.create({
            component: PdfConfigurationPage,
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        return data; // This should be the selected PDF configuration options
    }

	private handlePdfExport(
        tables: FileExporterTableMetadata[],
        exportedRecordMetadata: ExportedRecordMetadata,
        reportName: string,
        pdfConfig: PdfConfigData
    ): void {
        this.pdfFileExporterService
            .fetchFileExportData$(tables, exportedRecordMetadata)
            .pipe(first())
            .subscribe(
                async (tablesExpandableCards: TableExpandableCards[]) =>
                    await this.pdfFileExporterService.exportDataWithReferences(
                        tablesExpandableCards, 
                        reportName, 
                        pdfConfig.numFrozenColumns, 
                        pdfConfig.selectedPageOrientation, 
                        pdfConfig.selectedPaperSize,
						pdfConfig.userDefinedMaxLength
                    )
            );
    }

	private handleFileExport(
		option: string,
		tables: FileExporterTableMetadata[],
		exportedRecordMetadata: ExportedRecordMetadata,
		reportName: string
	): void {
		switch (option) {
			case FileExporterOption.xlsx:
				this.excelFileExporterService
					.fetchFileExportData$(tables, exportedRecordMetadata)
					.pipe(first())
					.subscribe(
						async (tablesExpandableCards: TableExpandableCards[]) =>
							await this.excelFileExporterService.exportData(tablesExpandableCards, reportName)
					);
				break;
			case FileExporterOption.csv:
				console.log(tables, "ini table")
				this.csvFileExporterService
					.fetchFileExportData$(tables, exportedRecordMetadata)
					.pipe(first())
					.subscribe(
						async (tablesExpandableCards: TableExpandableCards[]) =>
							await this.csvFileExporterService.exportData(tablesExpandableCards)
					);
				break;
			case FileExporterOption.none:
				break;
			default:
				this.toastService.presentError(
					this.translateService.instant('entry.export.errorMessages.operationCanceled'),
					ToastDuration.Fast
				);
				break;
		}
	}
}
